import { NavLink, Link } from 'react-router';
import { cn } from '@/lib/cn';
import { BraindayMark } from '@/components/brand';
import {
  Avatar,
  Badge,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  Eyebrow,
  Tag,
} from '@/components/ui';
import { NAV_ITEMS } from './navItems';
import { QuickCaptureInput } from './QuickCaptureInput';
import { useTaskStore } from '@/stores/taskStore';
import { useAuthStore } from '@/stores/authStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { countInboxTasks } from '@/features/inbox/inboxSelectors';
import { hapticNav } from '@/features/lifecycle/haptics';

export interface SidebarProps {
  collapsed: boolean;
  onCapture: (text: string) => void;
}

export function Sidebar({ collapsed, onCapture }: SidebarProps) {
  const primary = NAV_ITEMS.filter((i) => i.section === 'primary');
  const workspace = NAV_ITEMS.filter((i) => i.section === 'workspace');
  const inboxCount = useTaskStore((s) => countInboxTasks(s.tasks));

  const authMode = useAuthStore((s) => s.authMode);
  const displayName = useAuthStore((s) => s.displayName);
  const email = useAuthStore((s) => s.email);
  const isPro = useSubscriptionStore((s) => s.isPro);

  const isAuthed = authMode === 'authenticated';
  const accountName = displayName ?? email ?? (isAuthed ? 'You' : 'Guest');
  const accountHandle = isAuthed ? (email ?? 'Synced') : 'Sign in to sync';
  const avatarInitial = (displayName ?? email)?.trim().charAt(0) ?? (isAuthed ? 'Y' : 'G');

  return (
    <TooltipProvider delayDuration={300} skipDelayDuration={120}>
      <aside
        aria-label="Sidebar"
        className={cn(
          'flex h-full flex-col border-r border-line bg-canvas',
          collapsed ? 'items-center px-2 py-3' : 'px-3 py-3',
        )}
      >
        <div
          className={cn('flex items-center gap-2', collapsed ? 'justify-center' : 'mb-3 px-1.5')}
        >
          <BraindayMark size={34} />
          {!collapsed && (
            <>
              <span className="text-body-xl font-semibold tracking-tight text-t1">Brainday</span>
              {isPro && (
                <Tag variant="med" className="ml-auto">
                  Pro
                </Tag>
              )}
            </>
          )}
        </div>

        {!collapsed && (
          <div className="mb-4">
            <QuickCaptureInput onCapture={onCapture} />
          </div>
        )}

        <nav aria-label="Workspace" className="flex flex-1 flex-col gap-0.5">
          {primary.map((item) => {
            const props: React.ComponentProps<typeof NavItemLink> = {
              item,
              collapsed,
            };
            if (item.to === '/app/inbox' && inboxCount > 0) {
              props.badgeOverride = inboxCount;
            }
            return <NavItemLink key={item.to} {...props} />;
          })}
          {!collapsed && <Eyebrow className="mt-4 px-1.5">Workspace</Eyebrow>}
          {workspace.map((item) => (
            <NavItemLink key={item.to} item={item} collapsed={collapsed} />
          ))}
        </nav>

        {import.meta.env.DEV && !collapsed && (
          <div className="mt-2 border-t border-line pt-2">
            <Eyebrow className="px-1.5">Dev</Eyebrow>
            <Link
              to="/dev/ai-test"
              className="flex items-center gap-2.5 rounded-md px-2 py-1 text-caption text-t3 can-hover:hover:bg-card can-hover:hover:text-t2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
            >
              /dev/ai-test
            </Link>
          </div>
        )}

        <div
          className={cn(
            'mt-auto flex items-center gap-2 pt-3',
            collapsed ? 'justify-center' : 'px-1.5',
          )}
        >
          <Avatar size="sm" fallback={avatarInitial} />
          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate text-body font-medium text-t1">{accountName}</div>
              <div className={cn('truncate text-2xs text-t3', isAuthed && 'font-mono')}>
                {accountHandle}
              </div>
            </div>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}

function NavItemLink({
  item,
  collapsed,
  badgeOverride,
}: {
  item: (typeof NAV_ITEMS)[number];
  collapsed: boolean;
  badgeOverride?: number;
}) {
  const Icon = item.icon;
  const effectiveBadge = badgeOverride ?? item.badge;
  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/app'}
      onClick={() => {
        void hapticNav();
      }}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-body text-t2 can-hover:hover:bg-card',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          isActive && 'bg-card text-t1',
          collapsed && 'justify-center px-0',
        )
      }
    >
      <Icon className="h-4 w-4" aria-hidden />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {effectiveBadge !== undefined && <Badge>{effectiveBadge}</Badge>}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <TooltipRoot>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </TooltipRoot>
    );
  }
  return link;
}
